import { useMemo } from "react";
import { getVisitedPages } from "./PageTracker";

/**
 * =========================================================
 * 🔥 DCS OPERATOR COMPETENCY ENGINE
 * =========================================================
 */

export function useCompetencyEngine(activeAlarms = []) {

  return useMemo(() => {

    const alarms = activeAlarms || [];

    // =========================
    // PAGE NAVIGATION ANALYSIS
    // =========================

    const visitedPages =
      getVisitedPages() || [];

    const uniquePages =
      [...new Set(visitedPages)];

    const criticalPages = [

      "CrudeTank1",
      "BlendingSystem",
      "PreFlashTower",
      "Furnace",
      "ProductStripper",
      "MainFractionationTowers",
      "Flare"

    ];

    const missedCriticalPages =
      criticalPages.filter(
        p => !uniquePages.includes(p)
      );

    // =========================
    // ALARM ANALYSIS
    // =========================

    let hh = 0;
    let h = 0;
    let l = 0;
    let ll = 0;

    let muted = 0;

    const hasAlarms =
      alarms.length > 0;

    const alarmList = alarms.map(a => {

      const severity =
        a.severity || a.type || "L";

      if (severity === "HH") hh++;
      else if (severity === "H") h++;
      else if (severity === "L") l++;
      else if (severity === "LL") ll++;

      if (a.buzzerOn === false) muted++;

      return `• ${a.tag} (${severity}) - ${a.description}`;

    }).join("\n");

    // =========================
    // SCORING MODEL
    // =========================

    let score = 100;

    // Alarm penalties
    score -= hh * 15;
    score -= h * 10;
    score -= l * 5;
    score -= ll * 3;

    // Muted alarm penalty
    score -= muted * 5;

    // Missed page penalty
    score -= missedCriticalPages.length * 5;

    // Clamp score
    if (score < 0) score = 0;

    // =========================
    // STATUS
    // =========================

    let status = "EXCELLENT";

    if (score < 95) status = "GOOD";
    if (score < 85) status = "WARNING";
    if (score < 60) status = "POOR";
    if (score < 40) status = "CRITICAL";

    // =========================
    // NO ALARMS CASE
    // =========================

    if (!hasAlarms) {

      return {

        status,
        score,

        statement: `
The current operational session indicates stable plant interaction with no active process alarms detected.

This reflects strong operational discipline, effective monitoring awareness, and proper system understanding.

The operator demonstrates high competency in maintaining safe and stable process conditions.

No abnormal conditions were detected during this session.

Visited Operational Pages:
${uniquePages.length > 0
  ? uniquePages.join(", ")
  : "No operational pages visited."}

Critical Pages Missed:
${missedCriticalPages.length > 0
  ? missedCriticalPages.join(", ")
  : "None"}

${missedCriticalPages.length > 0
  ? "Some critical operational sections were not accessed during this session. This may indicate incomplete operational monitoring coverage."
  : "The operator successfully accessed all critical operational sections required for plant monitoring and control."
}
        `.trim() 

      };
    }

    // =========================
    // FULL STATEMENT
    // =========================

    const statement = `
The current session contains ${alarms.length} active alarm(s):

${alarmList}

Alarm Breakdown:
- HH: ${hh}
- H: ${h}
- L: ${l}
- LL: ${ll}
- Muted Alarms: ${muted}

This indicates abnormal operating conditions requiring operator attention.

${muted > 0
  ? "Muted alarms detected — this may indicate reduced alarm response discipline."
  : "Alarm handling discipline appears acceptable."
}

Operators are advised to acknowledge, investigate, and clear all alarms promptly.

Visited Operational Pages:
${uniquePages.length > 0
  ? uniquePages.join(", ")
  : "No operational pages visited."}

Critical Pages Missed:
${missedCriticalPages.length > 0
  ? missedCriticalPages.join(", ")
  : "None"}

${missedCriticalPages.length > 0
  ? "The operator did not access several critical operational sections of the plant. This may indicate incomplete operational awareness or improper startup/monitoring procedure."
  : "The operator successfully accessed all critical operational sections required for plant monitoring and control."
}
    `.trim();

    // =========================
    // RETURN
    // =========================

    return {
      status,
      score,
      statement
    };

  }, [activeAlarms]);

}